import { Fragment, useMemo, useState } from 'react'

import { HIGHLIGHT_TAG_MAP } from '../../../../../model/caf.interface'
import HighlightPinCard, { HighlightPinCardProps } from '../../overview/HighlightPinCard'
import HighlightViewCard from '../../overview/HighlightViewCard'

import { useToast } from '@repo/design-system/index'
import { format, isValid, subMonths } from 'date-fns'
import { useRouter } from 'next/router'
import { toast } from 'react-toastify'
import { useCafPinnedV5 } from '../../../../../modules/apis/webview/caf'
import useIsWebview from '../../../../../modules/hooks/common/useIsWebview'
import useMapTagToWording from '../../../../../modules/hooks/webview/caf/common/useMapTagToWording'
import useWebviewNavigation from '../../../../../modules/hooks/webview/common/useWebviewNavigation'
import { n_caf } from '../../../../../modules/i18n/strings/auto/wording_webview'
import useTranslation from '../../../../../modules/i18n/useTranslation'
import urls from '../../../../../modules/urls'
import { LESSON_ACTIVITIES_MAP, useLessonsStack } from '../../../../../pages/[lang]/webview/lessons'

/*  
    활성화된 filter 종류에 따라 다른 기준으로 data 정렬
    1. 링글 추천순 : weak/strength -> zscore_sum가 음수이면 weak, 양수이면 strength
    2. 구간 많은순 : average_count 순서로 정렬
    3. 카테고리 : tag 순서로 정렬(complexity, accuracy, fluency)
*/

const SortResultList = ({
  filterId,
  dataArr,
  detail = false,
  edit = false,
  refetch,
  from,
  startDate = '',
  endDate = '',
}: any) => {
  const { t, locale } = useTranslation()
  const router = useRouter()
  const { startDateFromUrl, endDateFromUrl } = router.query
  const isWebview = useIsWebview()
  const { useLessonsFlow } = useLessonsStack()
  const { push } = useLessonsFlow()
  const { toastSuccess } = useToast()
  const { mutate } = useCafPinnedV5()

  const checkPinnedHighlight = (tag) => {
    const filtered = dataArr.filter((item) => item.is_pinned).length
    const current = dataArr.findIndex((item) => item.tag === tag)
    if (current === -1) return
    const currentItem = dataArr[current]
    if (!currentItem.is_pinned && filtered >= 3) {
      toast(t(n_caf.highlight_list_edit.toast3))
      return
    } else {
      const currentValue = currentItem.is_pinned
      mutate(
        { tag, active: !currentValue },
        {
          onSuccess: () => {
            if (!currentValue) {
              isWebview ? toastSuccess(t(n_caf.highlight_list_edit.toast1)) : toast(t(n_caf.highlight_list_edit.toast1))
            } else {
              isWebview ? toastSuccess(t(n_caf.highlight_list_edit.toast2)) : toast(t(n_caf.highlight_list_edit.toast2))
            }
            refetch()
          },
        }
      )
    }
  }
  // 1. 링글 추천순 : weak/strength -> zscore_sum가 양수이면 weak, 음수이면 strength
  const useSortRecommended = (arr) => {
    const weakArr = useMemo(() => arr?.filter((item) => item.zscore_sum > 0 && item.average_count), [arr]).sort(
      (a, b) => b.zscore_sum - a.zscore_sum
    )
    const strengthArr = useMemo(
      () => arr?.filter((item) => item.zscore_sum <= 0 || item.average_count === 0),
      [arr]
    ).sort((a, b) => a.zscore_sum - b.zscore_sum)

    return {
      title: [
        {
          left: <span>{t(n_caf.highlight_list.weakness1)}</span>,
          right: <span></span>,
        },
        {
          left: <span>{t(n_caf.highlight_list.strength1)}</span>,
          right: <span></span>,
        },
      ],
      data: [weakArr, strengthArr],
    }
  }
  const sortCount = (arr) => {
    const copy = arr?.slice()
    const countArr = copy?.sort((a, b) => b.average_count - a.average_count)

    return {
      title: null,
      data: [countArr],
    }
  }
  const sortCategory = (arr) => {
    const comArr = arr?.filter(
      (item) =>
        item.tag == HIGHLIGHT_TAG_MAP['repetition'] ||
        item.tag == HIGHLIGHT_TAG_MAP['ros'] ||
        item.tag == HIGHLIGHT_TAG_MAP['sentence_structure']
    )
    const accArr = arr?.filter((item) => item.tag <= 12)
    const fluArr = arr?.filter(
      (item) => item.tag == HIGHLIGHT_TAG_MAP['speech_rate'] || item.tag == HIGHLIGHT_TAG_MAP['filler_word']
    )
    const pronunciationArr = arr?.filter((item) => item.tag == HIGHLIGHT_TAG_MAP['pronunciation'])
    return {
      title: [
        {
          left: <span>{t(n_caf.highlight_list.complexity1)}</span>,
          right: <span>{t(n_caf.highlight_list.complexity2)}</span>,
        },
        {
          left: <span>{t(n_caf.highlight_list.accuracy1)}</span>,
          right: <span>{t(n_caf.highlight_list.accuracy2)}</span>,
        },
        {
          left: <span>{t(n_caf.highlight_list.fluency1)}</span>,
          right: <span>{t(n_caf.highlight_list.fluency2)}</span>,
        },
        {
          left: <span>{t(n_caf.highlight_list.pronunciation1)}</span>,
          right: <span>{t(n_caf.highlight_list.pronunciation2)}</span>,
        },
      ],
      data: [comArr, accArr, fluArr, pronunciationArr],
    }
  }

  // 활성화된 filter 종류에 따라 다른 기준으로 data 정렬
  // 분류되는 카테고리에 따라 독립된 배열로 나눔
  const filterList = {
    recommended: useSortRecommended(dataArr),
    count: sortCount(dataArr),
    category: sortCategory(dataArr),
  }
  const { navigate } = useWebviewNavigation()

  const goToHighlightPerTagPage = (tagId, startDate, endDate, highlightId) => {
    if (from !== 'statistic') {
      // overview
      if (isWebview) {
        push(LESSON_ACTIVITIES_MAP.AIHighlightDetailActivity, { highlightId, tag: tagId })
      } else {
        const url = urls.webview.caf.highlight.detail(highlightId, tagId)
        navigate({ url })
      }
    } else {
      // statistics
      if (isWebview) {
        if (isValid(new Date(startDate)) && isValid(new Date(endDate))) {
          push(LESSON_ACTIVITIES_MAP.AIStatisticHighlightDetailActivity, {
            tagId,
            startDate: format(startDate, 'yyyy-MM-dd'),
            endDate: format(endDate, 'yyyy-MM-dd'),
          })
        } else {
          push(LESSON_ACTIVITIES_MAP.AIStatisticHighlightDetailActivity, {
            tagId,
            startDate: format(subMonths(new Date(), 1), 'yyyy-MM-dd'),
            endDate: format(new Date(), 'yyyy-MM-dd'),
          })
        }
      } else {
        const url = urls.webview.caf.statistic.highlight.tag(tagId, startDate, endDate)
        navigate({ url })
      }
    }
  }

  const { countWordingPerTag, namePerTag, iconPerTag } = useMapTagToWording(detail)

  return filterList[filterId]?.data.map((item, idx1) => (
    <Fragment key={idx1 + 'result'}>
      {filterList[filterId]?.title && (
        <h3 className={styles.title.box}>
          <span className={styles.title.left}> {filterList[filterId]?.title[idx1].left}</span>
          {locale === 'ko' && <span className={styles.title.right}>{filterList[filterId]?.title[idx1].right}</span>}
        </h3>
      )}
      {item?.map((highlightItem, idx2) => {
        return (
          <div key={idx2 + 'highlight_item'}>
            {edit ? (
              <OptimisticHighlightPinCard
                key={idx2 + 'highlight_edit'}
                upperTitle={{
                  text: namePerTag(highlightItem.tag),
                  icon: iconPerTag(highlightItem.tag),
                }}
                isActive={highlightItem.average_count > 0}
                mainText={
                  highlightItem.average_count > 0
                    ? countWordingPerTag(highlightItem.tag, highlightItem.average_count).count
                    : countWordingPerTag(highlightItem.tag).noCount
                }
                onClick={checkPinnedHighlight}
                isSelected={highlightItem.is_pinned}
                tag={highlightItem.tag}
              />
            ) : (
              <>
                <HighlightViewCard
                  key={idx2 + 'highlight_edit'}
                  isActive={highlightItem.average_count > 0}
                  upperTitle={{
                    text: namePerTag(highlightItem.tag),
                    icon: iconPerTag(highlightItem.tag),
                  }}
                  mainText={
                    highlightItem.average_count > 0
                      ? countWordingPerTag(highlightItem.tag, highlightItem.average_count).count
                      : countWordingPerTag(highlightItem.tag).noCount
                  }
                  onClick={() =>
                    goToHighlightPerTagPage(
                      highlightItem.tag,
                      startDate ?? startDateFromUrl,
                      endDate ?? endDateFromUrl,
                      highlightItem.id
                    )
                  }
                />
              </>
            )}
          </div>
        )
      })}
    </Fragment>
  ))
}

const OptimisticHighlightPinCard = (props: HighlightPinCardProps) => {
  const [isPinned, setIsPinned] = useState(!!props?.isSelected)
  const { onClick } = props
  const onClickCard = (tag) => {
    setIsPinned(!isPinned)
    onClick(tag)
  }
  return (
    <>
      <HighlightPinCard {...props} isSelected={isPinned} onClick={onClickCard} />
    </>
  )
}

const styles = {
  countText: `text-sBody flex items-center justify-center text-gray-700 `,
  title: {
    box: `mb-[16px] mt-[24px] flex items-center`,
    left: `mr-[8px] text-[14px]  font-bold text-purple-500`,
    right: `text-[12px] text-gray-500 `,
  },
}

export default SortResultList
